import React, { useEffect } from 'react';
import { Card, CardContent, Grid, Typography, Box, Avatar } from '@mui/material';
import { typoBase, thinBold } from '../styles/typography';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PartiesCard = ({ file }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  
  // Extract parties from the file
  const clients = file?.parties?.filter(party => party.type === 'Client') || [];
  const oppositeParties = file?.parties?.filter(party => party.type === 'Opposite Party') || [];
  const payingParties = file?.parties?.filter(party => party.type === 'Paying Party') || [];
  
  // Debug: log the parties data structure
  useEffect(() => {
    if (file?.parties && file.parties.length > 0) {
      console.log("Party data structure:", file.parties[0]);
    }
  }, [file]);

  const handlePartyClick = (partyId) => {
    navigate(`/relationships/${partyId}`);
  };

  const renderParty = (party) => {
    // Check if party and party.name exist
    if (!party || !party.name) {
      return null;
    }

    // Handle different contract types properly
    const displayName = party.name.contractType === 'Individual'
      ? `${party.name.firstName || ''} ${party.name.middleName ? party.name.middleName + ' ' : ''}${party.name.lastName || ''}`
      : party.name.businessName || '';

    // Try multiple ways to find email
    let email = '';
    
    // Method 1: Check direct email property
    if (party.name.email) {
      email = party.name.email;
    } 
    // Method 2: Try finding email in the contacts array
    else if (Array.isArray(party.name.contacts) && party.name.contacts.length > 0) {
      const contactWithEmail = party.name.contacts.find(contact => contact.email);
      if (contactWithEmail) {
        email = contactWithEmail.email;
      }
    }

    
    // Debug: log the email finding process
    console.log(`Party ${party.name._id}: Name=${party.name.firstName || party.name.businessName}, Email=${email}, Has contacts:`, 
                Array.isArray(party.name.contacts) ? party.name.contacts.length : 'none');
    
    // Get phone from contacts array if available
    let phone = '';
    if (Array.isArray(party.name.contacts) && party.name.contacts.length > 0) {
      const contactWithPhone = party.name.contacts.find(contact => contact.phone);
      if (contactWithPhone) {
        phone = contactWithPhone.phone;
      }
    }

    return (
      <Box 
        key={party.name._id || party._id} 
        sx={{ 
          borderBottom: '1px solid #ECECEC', 
          padding: '8px 0',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center">
            <Avatar
              alt={displayName}
              src={party.name.avatar || "/static/images/avatar/1.jpg"}
              sx={{
                width: "20px",
                height: "20px",
                marginRight: "10px",
              }}
            />
            <Typography
              variant="body1"
              onClick={() => handlePartyClick(party.name._id)}
              style={{ 
                ...typoBase, 
                fontWeight: 100, 
                fontSize: "10.5px",
                color: '#005b92',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
              sx={{
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              {displayName}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center">
            {email && (
              <Box display="flex" alignItems="center" mr={2}>
                <MailOutlineIcon sx={{ fontSize: '14px', color: '#777', marginRight: '5px' }} />
                <Typography variant="caption" style={{ color: '#777', fontSize: '11px' }}>
                  {email}
                </Typography>
              </Box>
            )}
            
            {phone && (
              <Box display="flex" alignItems="center">
                <PhoneIcon sx={{ fontSize: '14px', color: '#777', marginRight: '5px' }} />
                <Typography variant="caption" style={{ color: '#777', fontSize: '11px' }}>
                  {phone}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {clients.length > 0 && (
        <Box mb={2}>
          <Typography
            variant="subtitle1"
            style={{ ...thinBold, color: "#404040", marginBottom: "8px" }}
          >
            {t('common:clients')}
          </Typography>
          {clients.map(renderParty)}
        </Box>
      )}

      {oppositeParties.length > 0 && (
        <Box mb={2}>
          <Typography
            variant="subtitle1"
            style={{ ...thinBold, color: "#404040", marginBottom: "8px" }}
          >
            {t('common:opposite_parties')}
          </Typography>
          {oppositeParties.map(renderParty)}
        </Box>
      )}

      {payingParties.length > 0 && (
        <Box mb={2}>
          <Typography
            variant="subtitle1"
            style={{ ...thinBold, color: "#404040", marginBottom: "8px" }}
          >
            {t('common:paying_parties')}
          </Typography>
          {payingParties.map(renderParty)}
        </Box>
      )}

      {clients.length === 0 && oppositeParties.length === 0 && payingParties.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={2}>
          <Typography variant="body1" style={{ color: '#777' }}>
            {t('common:no_data_available')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PartiesCard; 